import { useAuth } from '@/hooks/useAuth';
import { Navigate, useLocation } from 'react-router-dom'; // Make sure useLocation is imported
import { useEffect, useState } from 'react';

import Loader from '@/components/Loader';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

import { supabase } from '@/integrations/supabase/client';

import { IdleTimeoutProvider } from '@/components/security/IdleTimeoutProvider';

const ProtectedRoute = ({ children, adminOnly = false }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [mfaLocked, setMfaLocked] = useState<boolean | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (user) {
      supabase.auth.mfa.getAuthenticatorAssuranceLevel().then(({ data }) => {
        if (isMounted) {
          if (data?.nextLevel === 'aal2' && data?.currentLevel === 'aal1') {
            setMfaLocked(true);
          } else {
            setMfaLocked(false);
          }
        }
      });
    } else {
      setMfaLocked(false);
    }
    return () => { isMounted = false; };
  }, [user]);

  if (loading || mfaLocked === null) {
    return <Loader />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (mfaLocked) {
    // If they only have password verified, kick them back to the 2FA challenge.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <IdleTimeoutProvider>
      {children}
    </IdleTimeoutProvider>
  );
};

export default ProtectedRoute;